import { Fragment, FunctionComponent } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import get from 'lodash.get';

import { LazyImage } from '../../LazyImage/LazyImage';
import { ImageSizeType } from '../../types/StyleSizes';
import styles from '../styles.scss';

import { getSrcSetString } from '../../helpers/srcSet';
import { SeoLink } from 'dibs-elements/exports/SeoLink';
import { ThumbImage_photo } from './__generated__/ThumbImage_photo.graphql';

export type ImageProps = {
    disableLinks?: boolean;
    onClick?: () => void;
    pdpUrl: string;
    title: string;
    containerClassName?: string;
    srcSetSizes?: string;
    srcSetWidths?: Array<number>;
    srcSetQuality?: number;
    useLoFiLazyLoader: boolean;
    imageSize: ImageSizeType;
    imageLoadVerticalOffset?: number;
    onContentLoaded?: () => void;
};

type Props = ImageProps & {
    photo: ThumbImage_photo;
    index: number;
    useEagerImageLoad: boolean;
};

export const ThumbImageComponent: FunctionComponent<Props> = ({
    photo,
    index,
    useEagerImageLoad,
    containerClassName,
    disableLinks,
    onClick,
    pdpUrl,
    srcSetSizes,
    srcSetWidths,
    srcSetQuality,
    imageSize,
    title,
    imageLoadVerticalOffset,
    onContentLoaded,
    useLoFiLazyLoader,
}) => {
    const smallPath = get(photo, 'smallPath');
    const placeholder = get(photo, 'placeholder');
    const masterPath = get(photo, 'masterOrZoomPath') || '';

    const imgSource = smallPath || masterPath;

    const srcSetAttributes =
        srcSetSizes && masterPath
            ? {
                  srcSet: getSrcSetString(masterPath, {
                      widths: srcSetWidths,
                      quality: srcSetQuality,
                  }),
                  sizes: srcSetSizes,
              }
            : {};

    const image = (
        <div className={containerClassName} data-tn="product-image-container">
            {useEagerImageLoad ? (
                <img
                    className={styles.swiperImage}
                    alt={title}
                    data-tn="product-image"
                    {...srcSetAttributes}
                    src={imgSource}
                />
            ) : (
                <Fragment>
                    <LazyImage
                        className={styles.swiperImage}
                        srcSetAttributes={srcSetAttributes}
                        placeholder={placeholder}
                        alt={title}
                        offsetVertical={imageLoadVerticalOffset}
                        onImageLoad={onContentLoaded}
                        useLoFiLazyLoader={useLoFiLazyLoader}
                        src={imgSource}
                        imageSize={imageSize}
                    />
                    {index === 0 && (
                        <noscript>
                            <img
                                className={styles.swiperImage}
                                alt={title}
                                {...srcSetAttributes}
                                src={imgSource}
                            />
                        </noscript>
                    )}
                </Fragment>
            )}
        </div>
    );

    const imageWithLink = (
        <SeoLink
            dataTn="thumb-image-link"
            linkData={{ path: pdpUrl, isLinkable: false }}
            onClick={onClick}
        >
            {image}
        </SeoLink>
    );

    return disableLinks ? image : imageWithLink;
};

export const ThumbImage = createFragmentContainer(ThumbImageComponent, {
    photo: graphql`
        fragment ThumbImage_photo on ItemPhoto {
            placeholder
            smallPath
            masterOrZoomPath
        }
    `,
});
